import React, { useState, useEffect } from 'react';
import { Input, Button, Typography, Space, Carousel, Checkbox, Grid, Select, Alert, message } from 'antd';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
    UserOutlined, ShopOutlined, MailOutlined, LockOutlined, PhoneOutlined, CloudOutlined, SyncOutlined,
    ThunderboltOutlined, StarOutlined, CrownOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../aws/AuthContext';
import './styleCrearUser.css';

const { Text } = Typography;
const { useBreakpoint } = Grid;

// --- DICCIONARIO DE ERRORES DE REGISTRO (AWS COGNITO) ---
const obtenerMensajeErrorRegistro = (error) => {
    if (!error || !error.code) return 'Ocurrió un error inesperado al intentar crear tu cuenta.';

    switch (error.code) {
        case 'UsernameExistsException':
            return 'Este correo electrónico ya está registrado. Si olvidaste tu contraseña, ve a Iniciar Sesión y recupérala.';
        case 'InvalidPasswordException':
            return 'La contraseña es muy débil. Asegúrate de incluir al menos 8 caracteres, una mayúscula, un número y un carácter especial (ej. @, $, !).';
        case 'InvalidParameterException':
            return 'Revisa los datos ingresados. Asegúrate de que el formato del correo y el teléfono sean correctos.';
        case 'CodeMismatchException':
            return 'El código de verificación es incorrecto.';
        case 'ExpiredCodeException':
            return 'El código de verificación ha expirado. Deberás solicitar uno nuevo.';
        default:
            return `Error de conexión: ${error.message}`;
    }
};

const CrearUsuarioApp = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const [paso, setPaso] = useState(1);
    const [plan, setPlan] = useState('basico');

    useEffect(() => {
        const planUrl = searchParams.get('plan');
        if (planUrl === 'basico' || planUrl === 'premium') {
            setPlan(planUrl);
        }
    }, [searchParams]);

    const { signUp, confirmCorreo, login, getJwtToken, resendSignUpCode, session, fetchFullProfile } = useAuth();
    const screens = useBreakpoint();
    const isMobile = screens.md === false;

    const [loading, setLoading] = useState(false);
    
    // Datos Paso 1
    const [nombre, setNombre] = useState('');
    const [correo, setCorreo] = useState('');
    const [contraseña, setContraseña] = useState('');
    const [confirmarContraseña, setConfirmarContraseña] = useState('');
    const [telefono, setTelefono] = useState('');
    const [aceptarTerminos, setAceptarTerminos] = useState(false);
    const [codigoVerificacion, setCodigoVerificacion] = useState('');
    const [empresa_nombre, setEmpresa_nombre] = useState('');
    const [msNombre, setMsNombre] = useState(false);
    const [msCorreo, setMsCorreo] = useState(false);
    const [msContraseña, setMsContraseña] = useState(false);
    const [msConfirmar, setMsConfirmar] = useState(false);
    const [msTelefono, setMsTelefono] = useState(false);
    const [msEmpresa_nombre, setMsEmpresa_nombre] = useState(false);
    const [msCorreoValido, setMsCorreoValido] = useState(false);
    const [msTerminos, setMsTerminos] = useState(false);
    
    const [errorRegistro, setErrorRegistro] = useState(null);
    const [errorVerificacion, setErrorVerificacion] = useState(null);

    const validarCorreo = (email) => {
        const expresionRegular = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return expresionRegular.test(email);
    };

    // ==========================================
    // PASO 1: CREAR CUENTA
    // ==========================================
    const crearUsuario = async () => {
        setErrorRegistro(null); 
        
        if (!nombre || !correo || !contraseña || !confirmarContraseña || !telefono) {
            if (!nombre) setMsNombre(true);
            if (!correo) setMsCorreo(true);
            if (!contraseña) setMsContraseña(true);
            if (!confirmarContraseña) setMsConfirmar(true);
            if (!telefono) setMsTelefono(true);
            return;
        }
        if (!validarCorreo(correo)) { setMsCorreoValido(true); return; }
        
        if (contraseña !== confirmarContraseña) {
            setErrorRegistro("Las contraseñas no coinciden. Por favor, verifícalas.");
            return;
        }
        if (contraseña.length < 8) {
            setErrorRegistro("La contraseña debe tener mínimo 8 caracteres.");
            return;
        }

        if (!aceptarTerminos) { setMsTerminos(true); return; }

        setLoading(true);
        let numTel = '+57' + telefono;

        try {
            await signUp(correo, contraseña, { nombre, telefono: numTel });
            setPaso(2); 
        } catch (error) {
            console.error('Error registering user:', error);
            if (error.code === 'UsernameExistsException') {
                try {
                    await resendSignUpCode(correo);
                    message.info('Detectamos un registro incompleto. Te hemos enviado un nuevo código.');
                    setPaso(2);
                } catch (resendError) {
                    setErrorRegistro('Este correo electrónico ya está registrado y activo. Por favor, ve a Iniciar Sesión.');
                }
            } else {
                setErrorRegistro(obtenerMensajeErrorRegistro(error));
            }
        } finally {
            setLoading(false);
        }
    };

    // ==========================================
    // PASO 2: VERIFICAR CÓDIGO
    // ==========================================
    const verificarCodigo = async () => {
        setErrorVerificacion(null);
        if (!codigoVerificacion) { setErrorVerificacion("Por favor, ingresa el código."); return; }
        if (codigoVerificacion.length !== 6) { setErrorVerificacion("El código debe tener exactamente 6 dígitos."); return; }

        setLoading(true);
        try {
            const isConfirmed = await confirmCorreo(correo, codigoVerificacion);
            if (!isConfirmed) {
                setErrorVerificacion("No se pudo confirmar el código.");
                setLoading(false);
                return;
            }

            await login(correo, contraseña);
            
            message.success("¡Cuenta verificada! Solo falta un paso.");
            setPaso(3); 
        } catch (error) {
            console.error("Error en verificación:", error);
            setErrorVerificacion(obtenerMensajeErrorRegistro(error));
        } finally {
            setLoading(false);
        }
    };

    // ==========================================
    // PASO 3: ONBOARDING EMPRESA
    // ==========================================
    const finalizarOnboarding = async () => {
        if (!empresa_nombre) {
            setMsEmpresa_nombre(true);
            return;
        }

        setLoading(true);
        try {
            let token = await getJwtToken();

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/v1-onboarding`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ 
                        empresa_nombre: empresa_nombre, 
                        plan: plan,
                        fecha_acepta_terminos: new Date().toISOString(), 
                        version_terminos: "1.0.0" 
                    })
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Error configurando tu empresa.");
            }

            await fetchFullProfile();

            message.success(`¡Bienvenido a Yugonub, ${nombre.split(' ')[0]}!`);
            navigate("/ventas");
        } catch (error) {
            console.error("Error completo en onboarding:", error);
            message.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (nombre !== '') setMsNombre(false); }, [nombre]);
    useEffect(() => { if (correo !== '') { setMsCorreo(false); setMsCorreoValido(false); } }, [correo]);
    useEffect(() => { if (contraseña !== '') setMsContraseña(false); }, [contraseña]);
    useEffect(() => { if (confirmarContraseña !== '') setMsConfirmar(false); }, [confirmarContraseña]);
    useEffect(() => { if (telefono !== '') setMsTelefono(false); }, [telefono]);
    useEffect(() => { if (empresa_nombre !== '') setMsEmpresa_nombre(false); }, [empresa_nombre]);

    if (session) {
        return <Navigate to="/ventas" replace />;
    }

    return (
        <div className="register-wrapper">
            <div className="register-ad-column">
                <Carousel autoplay effect="fade" autoplaySpeed={5000}>
                    <div className="reg-ad-slide slide-reg-1">
                        <div className="reg-ad-overlay">
                            <CloudOutlined className="reg-ad-icon" />
                            <h2>Todo en la Nube</h2>
                            <p>Accede a tu información contable y de inventario desde cualquier lugar del mundo.</p>
                        </div>
                    </div>
                    <div className="reg-ad-slide slide-reg-2">
                        <div className="reg-ad-overlay">
                            <SyncOutlined className="reg-ad-icon" />
                            <h2>Sincronización Total</h2>
                            <p>Tus ventas, gastos y stock actualizados en tiempo real en todos tus dispositivos.</p>
                        </div>
                    </div>
                    <div className="reg-ad-slide slide-reg-3">
                        <div className="reg-ad-overlay">
                            <ThunderboltOutlined className="reg-ad-icon" />
                            <h2>Rápido y Seguro</h2>
                            <p>Optimiza tus procesos diarios con una interfaz intuitiva diseñada para la velocidad.</p>
                        </div>
                    </div>
                </Carousel>
            </div>

            <div className="register-form-column">
                <div className="register-box">
                    <div className="register-header">
                        <a href='/'>
                            <img src="/img/g1.png" alt="Yugonub Logo" title='Página de inicio' style={{ width: !isMobile ? "340px" : "320px", height: !isMobile ? "58px" : "60px" }} />
                        </a>
                        <Text type="secondary" style={{ marginTop: '15px', display: 'block', fontSize: '15px' }}>
                            {paso === 1 && <Text type="secondary">Comienza tu prueba gratis de <span style={{ fontWeight: 'bold', color: 'black'}}>10 días</span> hoy mismo</Text>}
                            {paso === 2 && "Revisa tu bandeja de entrada"}
                            {paso === 3 && "Ya casi terminamos..."}
                        </Text>
                    </div>

                    {paso === 1 && (
                        <div className="inputs-area fade-in">
                            {errorRegistro && (
                                <Alert message="Atención" description={errorRegistro} type="error" showIcon style={{ marginBottom: '20px', borderRadius: '8px' }} />
                            )}

                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <div>
                                    <Text strong style={{ display: 'block', marginBottom: '8px', fontSize: '13px' }}>Tu Nombre (Dueño / Administrador)</Text>
                                    <Input size="large" prefix={<UserOutlined style={{ color: '#22BABB' }} />} placeholder="Ej: Juan Pérez" value={nombre} onChange={e => setNombre(e.target.value)} />
                                    {msNombre && <Text type="danger" style={{ fontSize: '12px' }}>Por favor, ingresa tu nombre.</Text>}
                                </div>

                                <div>
                                    <Input size="large" prefix={<MailOutlined style={{ color: '#22BABB' }} />} placeholder="Correo electrónico" value={correo} onChange={e => setCorreo(e.target.value)} />
                                    {msCorreo && <Text type="danger" style={{ fontSize: '12px' }}>Ingresa tu correo.</Text>}
                                    {msCorreoValido && <Text type="danger" style={{ fontSize: '12px' }}>El correo no tiene un formato válido.</Text>}
                                </div>

                                <div>
                                    <Input.Password size="large" prefix={<LockOutlined style={{ color: '#22BABB' }} />} placeholder="Crea una contraseña segura" value={contraseña} onChange={e => setContraseña(e.target.value)} />
                                    <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>Mínimo 8 caracteres, incluye mayúscula, números y símbolos (@, $, !)</Text>
                                    {msContraseña && <Text type="danger" style={{ fontSize: '12px' }}>Crea tu contraseña.</Text>}
                                </div>

                                <div>
                                    <Input.Password size="large" prefix={<LockOutlined style={{ color: '#22BABB' }} />} placeholder="Confirma tu contraseña" value={confirmarContraseña} onChange={e => setConfirmarContraseña(e.target.value)} />
                                    {msConfirmar && <Text type="danger" style={{ fontSize: '12px' }}>Debes confirmar tu contraseña.</Text>}
                                </div>

                                <div>
                                    <Input 
                                        size="large" 
                                        prefix={<PhoneOutlined style={{ color: '#22BABB' }} />} 
                                        placeholder="Teléfono celular" 
                                        value={telefono} 
                                        maxLength={10}
                                        onChange={e => setTelefono(e.target.value.replace(/\D/g, ''))} 
                                    />
                                    {msTelefono && <Text type="danger" style={{ fontSize: '12px' }}>Ingresa tu número celular.</Text>}
                                </div>

                                <div style={{ marginTop: '5px' }}>
                                    <Checkbox checked={aceptarTerminos} onChange={(e) => { setAceptarTerminos(e.target.checked); setMsTerminos(false); }}>
                                        <Text style={{ fontSize: '13px', color: '#595959' }}>
                                            Acepto los <Link to="/terminos-y-condiciones" target="_blank" style={{ color: '#1FA9FF' }}>Términos y Condiciones</Link> y la <Link to="/politica-privacidad" target="_blank" style={{ color: '#1FA9FF' }}>Política de Privacidad</Link>.
                                        </Text>
                                    </Checkbox>
                                    {msTerminos && (
                                        <div style={{ display: 'block', marginTop: '2px' }}>
                                            <Text type="danger" style={{ fontSize: '12px' }}>Debe aceptar los términos y condiciones para continuar.</Text>
                                        </div>
                                    )}
                                </div>

                                <Button disabled={!aceptarTerminos} title={!aceptarTerminos ? 'Debes aceptar términos' : ''} type="primary" block className="btn-yugonub-reg" onClick={crearUsuario} loading={loading}>
                                    CREAR CUENTA
                                </Button>
                            </Space>

                            <div className="login-link-container" style={{ marginTop: '20px', marginBottom: '20px', textAlign: 'center' }}>
                                <Text>¿Ya tienes una cuenta? <Link to="/login" style={{ color: '#1FA9FF', fontWeight: 'bold' }}>Iniciar Sesión</Link></Text>
                            </div>
                        </div>
                    )}

                    {paso === 2 && (
                        <div className="verification-area fade-in">
                            {errorVerificacion && (
                                <Alert message="Error de Verificación" description={errorVerificacion} type="error" showIcon style={{ marginBottom: '20px', borderRadius: '8px' }} />
                            )}

                            <Text style={{ display: 'block', marginBottom: 15, textAlign: 'center' }}>
                                Te enviamos un código de seguridad a: <br /> <b style={{ color: '#22BABB' }}>{correo}</b>
                            </Text>

                            <Input size="large" placeholder="Código de 6 dígitos" value={codigoVerificacion} maxLength={6} onChange={e => setCodigoVerificacion(e.target.value.replace(/\D/g, ''))} />

                            <Button type="primary" block className="btn-yugonub-reg" onClick={verificarCodigo} loading={loading} style={{ marginTop: '20px' }}>
                                VERIFICAR CÓDIGO
                            </Button>

                            <div style={{ marginTop: '15px', textAlign: 'center' }}>
                                <Button type="link" onClick={() => { setPaso(1); setCodigoVerificacion(""); setErrorRegistro(null); }}>
                                    ¿Te equivocaste de correo? Volver
                                </Button>
                            </div>
                        </div>
                    )}

                    {paso === 3 && (
                        <div className="inputs-area fade-in">
                            <Alert 
                                message={`¡Verificación exitosa!`} 
                                description="Tu cuenta es segura. Ahora, cuéntanos sobre tu negocio para preparar tu sistema." 
                                type="success" 
                                showIcon 
                                icon={<CheckCircleOutlined />}
                                style={{ marginBottom: '20px', borderRadius: '8px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }} 
                            />

                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <div>
                                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>Nombre de tu Negocio / Empresa</Text>
                                    <Input size="large" prefix={<ShopOutlined style={{ color: '#22BABB' }} />} placeholder="Ej: Ferretería El Constructor" value={empresa_nombre} onChange={e => setEmpresa_nombre(e.target.value)} />
                                    {msEmpresa_nombre && <Text type="danger" style={{ fontSize: '12px' }}>Por favor, ingresa el nombre de tu empresa.</Text>}
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>Plan a utilizar:</Text>
                                    <Select size="large" value={plan} onChange={value => setPlan(value)} style={{ width: '100%' }} suffixIcon={<StarOutlined style={{ color: '#22BABB' }} />}>
                                        <Select.Option value="basico">
                                            <Space><StarOutlined style={{ color: '#22BABB' }} /><span>Plan Básico ($35.000 / mes)</span></Space>
                                        </Select.Option>
                                        <Select.Option value="premium">
                                            <Space><CrownOutlined style={{ color: '#faad14' }} /><span>Plan Premium ($59.000 / mes)</span></Space>
                                        </Select.Option>
                                    </Select>
                                </div>

                                <Button type="primary" block className="btn-yugonub-reg" onClick={finalizarOnboarding} loading={loading}>
                                    FINALIZAR Y ENTRAR AL SISTEMA
                                </Button>
                            </Space>
                        </div>
                    )}
                </div>

                <div className="register-legal-fixed">
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-sm">
                        <Link style={{ color: '#747F8E', fontWeight: '500' }} to="/terminos-y-condiciones">Términos y Condiciones</Link>
                        <span className="hidden sm:inline text-gray-300">|</span>
                        <Link style={{ color: '#747F8E', fontWeight: '500' }} to="/politica-privacidad">Política de Privacidad</Link>
                        <span className="hidden sm:inline text-gray-300">|</span>
                        <Text style={{ color: '#747F8E' }} type="secondary">v1.0.0</Text>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CrearUsuarioApp;